import React, { useEffect, useState } from "react";
import { useFormat } from "@/contexts/FormatContext";
import { useSearchParams } from "react-router-dom";
import { api, formatValue, formatAssetValue } from "@/lib/api";
import { Loader2, Trophy, ChevronDown, ChevronUp, Lightbulb, Copy, Share2, Info, AlertTriangle, ArrowDownWideNarrow } from "lucide-react";
import { toast } from "sonner";
import DmeTag from "@/components/DmeTag";
import FormatBadge from "@/components/FormatBadge";
import PlayerBadge from "@/components/PlayerBadge";
import { Link } from "react-router-dom";
import { TeamPill, PosRank } from "@/components/PlayerHeadshot";

// One rostered player line — links to the player card, with team pill, positional rank,
// and the X-Factor/Superstar tier badge (consistent with the DME values board).
function RosterPlayer({ p, dim }) {
  const isAsset = ["QB", "RB", "WR", "TE"].includes(p.position) && (p.value || 0) > 0;
  return (
    <div className="flex items-center justify-between text-xs gap-2" data-testid={`pr-player-${p.player_id}`}>
      <div className="flex items-center gap-1.5 min-w-0">
        {isAsset ? (
          <Link to={`/app/players/${p.player_id}`} onClick={e => e.stopPropagation()}
            className={`truncate hover:text-[var(--accent)] ${dim ? "text-[var(--text-2)]" : ""}`}>{p.name}</Link>
        ) : (
          <span className={`truncate ${dim ? "text-[var(--text-2)]" : ""}`}>{p.name} <span className="text-[var(--text-3)]">· {p.position}</span></span>
        )}
        {isAsset && <PlayerBadge badge={p.badge} size={13} />}
        {p.pos_rank && <PosRank rank={p.pos_rank} className="hidden sm:inline-flex" />}
        {p.team && p.team !== "FA" && <TeamPill team={p.team} className="hidden sm:inline-flex" />}
      </div>
      <span className="tabular shrink-0">{formatAssetValue(p.value, p.position)}</span>
    </div>
  );
}

function pickLabel(pk) {
  const round = pk.slot
    ? `${pk.round}.${String(pk.slot).padStart(2, "0")}`
    : `R${pk.round}`;
  return `${pk.season} ${round}`;
}

// WIN-NOW contention tier — driven by the Monte-Carlo PLAYOFF ODDS the sim already produces,
// NOT a rigid rank split (which mislabeled teams sitting on either side of a third boundary,
// e.g. a 44%-odds team lumped as "Rebuilding" next to a 45% "On the Bubble" team):
//   Contender     = championship window open now (>= 60% odds)
//   On the Bubble = playoff push — a path in, but flawed (40–59%)
//   Rebuilding    = long shot (<= 39%)
// Falls back to a thirds split only when the sim hasn't produced playoff odds yet.
const TIER_CONTENDER = { label: "Contender", color: "#B45309" };
const TIER_BUBBLE = { label: "On the Bubble", color: "#0284C7" };
const TIER_REBUILD = { label: "Rebuilding", color: "#DC2626" };
function contenderTier(playoffPct, rank, total) {
  if (playoffPct == null) {
    if (!total || !rank) return null;
    if (rank <= Math.round(total / 3)) return TIER_CONTENDER;
    if (rank <= Math.round((2 * total) / 3)) return TIER_BUBBLE;
    return TIER_REBUILD;
  }
  if (playoffPct >= 60) return TIER_CONTENDER;
  if (playoffPct >= 40) return TIER_BUBBLE;
  return TIER_REBUILD;
}

// Roster-age health: green = young (<27), yellow = aging (27-27.9), red = old (>=28).
function ageColor(age) {
  if (age == null) return "var(--text-3)";
  if (age < 27) return "#22C55E";
  if (age < 28) return "#F59E0B";
  return "#EF4444";
}

// The poorly-ranked starting positions come straight from the backend (`weak_positions`),
// the SAME source that drives the "Why this ranking" banner — so the tier advice can never
// contradict it. Names up to two weak spots (e.g. "at WR & RB").
function weakSpotLabel(row) {
  const labels = (row.weak_positions || []).map(w => w.label);
  return labels.length ? ` at ${labels.join(" & ")}` : "";
}

// One-line strategic nudge driven by the tier + this roster's shape (picks/bench share).
function tierAdvice(row, tier, upcomingSeason) {
  if (!tier || !row.total_value) return null;
  const picksShare = (row.picks_value || 0) / row.total_value;
  const benchShare = (row.bench_value || 0) / row.total_value;
  const at = weakSpotLabel(row);
  const yr = upcomingSeason || new Date().getFullYear() + 1;
  if (row.underachiever) {
    return `Elite roster value but a soft weekly lineup — the sim projects a bottom-third finish (early pick). Firm up your flex/depth${at} to convert talent into wins, or sell a star at peak value.`;
  }
  if (tier.label === "Contender") {
    if (picksShare > 0.18) return `Win-now window is open — flip your stockpiled picks into a proven starter${at}.`;
    if (benchShare > 0.30) return `You have the core to compete — consolidate your deep bench into one elite starter${at}.`;
    return `You're built to win now — hunt a top-tier upgrade${at} rather than chasing depth.`;
  }
  if (tier.label === "On the Bubble") {
    if (picksShare > 0.18) return `Fringe contender — lean into a retool${at}; your pick capital can vault you up a tier.`;
    return `You're on the fence — a targeted upgrade${at} could tip you toward contention; otherwise lean into selling.`;
  }
  // Rebuilding
  if (benchShare > 0.30) return `Rebuild mode — sell win-now vets for ${yr} firsts and add ascending youth${at}; your bench depth is tradeable capital.`;
  return `Rebuild mode — move aging veterans now for ascending youth${at} and ${yr} first-round picks.`;
}

export default function PowerRankings() {
  const [params] = useSearchParams();
  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState(params.get("league") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [sortByTotal, setSortByTotal] = useState(false);
  const { format, resolvedFormat, setResolvedFormat } = useFormat();

  const sharePR = async () => {
    if (!leagueId) return;
    setSharing(true);
    try {
      const r = await api.post(`/leagues/${leagueId}/power-rankings/share`);
      await navigator.clipboard.writeText(r.data.url);
      toast.success("Public link copied — share your rankings anywhere");
    } catch { toast.error("Couldn't create a share link. Please try again."); }
    finally { setSharing(false); }
  };

  const copyGamePlan = async (r, tier, advice) => {
    const total = data?.rankings?.length || 0;
    const text = `${r.owner_name} · ${data?.league?.name || "League"} — Power Ranking #${r.rank}${total ? `/${total}` : ""} (${tier.label})\n${advice}\nvia Dynasty Metrics`;
    try { await navigator.clipboard.writeText(text); toast.success("Game plan copied — paste it in your league chat"); }
    catch { toast.error("Couldn't copy. Try selecting the text manually."); }
  };

  useEffect(() => {
    api.get("/leagues").then(r => {
      setLeagues(r.data.leagues || []);
      if (!leagueId && r.data.leagues?.[0]) setLeagueId(r.data.leagues[0].league_id);
    });
  }, []); // eslint-disable-line

  // Report the format Auto resolves to for this league, so the toggle shows "Auto · SF".
  useEffect(() => () => setResolvedFormat(null), [setResolvedFormat]);

  useEffect(() => {
    if (!leagueId) return;
    setLoading(true);
    api.get(`/leagues/${leagueId}/power-rankings`)
      .then(r => {
        setData(r.data);
        setResolvedFormat(r.data?.league?.format || null);
      })
      .finally(() => setLoading(false));
  }, [leagueId, format]); // eslint-disable-line

  const rows = data && sortByTotal
    ? [...data.rankings].sort((a, b) => (b.total_value || 0) - (a.total_value || 0))
    : (data?.rankings || []);

  return (
    <div className="space-y-6 fade-up">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1">// POWER RANKINGS</div>
          <h1 className="font-display text-4xl font-bold" data-testid="pr-heading">League Power Rankings</h1>
          {data?.league && (
            <div className="text-sm text-[var(--text-2)] mt-1 flex items-center gap-2" data-testid="pr-league-format">
              {data.league.name} <FormatBadge format={data.league.format || data.league.league_format} />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select className="input-dark md:w-72" value={leagueId} onChange={e => setLeagueId(e.target.value)} data-testid="pr-league-select">
            <option value="">— Pick a league —</option>
            {leagues.map(l => <option key={l.league_id} value={l.league_id}>{l.name}</option>)}
          </select>
          {leagueId && data && (
            <button onClick={sharePR} disabled={sharing} data-testid="pr-share-btn"
              className="btn-secondary text-sm inline-flex items-center gap-1.5 shrink-0 disabled:opacity-60"
              title="Create a public, no-login link to these rankings">
              {sharing ? <Loader2 className="w-4 h-4 spin" /> : <Share2 className="w-4 h-4" />} Share
            </button>
          )}
        </div>
      </header>

      {!loading && data && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-[var(--border)] bg-white/[0.02] px-3 py-2" data-testid="pr-methodology">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--accent)]" />
          <p className="text-[11px] text-[var(--text-3)] leading-relaxed">
            <span className="text-[var(--text-2)] font-semibold">How the standings are ranked:</span> teams are ordered by their
            <span className="text-[var(--text-2)]"> projected finish</span> — a 10,000-run Monte-Carlo season simulation off live weekly player projections (expected wins &amp; playoff odds). Tiers follow each team's <span className="text-[var(--text-2)]">playoff odds</span>: <span className="text-[var(--text-2)]">Contenders</span> have a championship window open now (≥60%), <span className="text-[var(--text-2)]">On the Bubble</span> are in the playoff push (40–59%), and <span className="text-[var(--text-2)]">Rebuilding</span> are long shots (≤39%).
            A <span style={{ color: "#F5B301" }}>Missing a Few Pieces</span> flag marks teams with elite roster value that still project to finish low — talent alone isn't translating to wins (usually a soft flex/depth). The <span className="text-[var(--text-2)]">Total</span> column shows overall asset value (players + picks) and won't always descend down the list — click the <span className="text-[var(--text-2)]">Total</span> header to re-sort by total assets and see the gap between win-now standing and raw asset value.
          </p>
        </div>
      )}

      {loading && (
        <div className="card-bordered p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>
      )}

      {!loading && data && (
        <div className="card-bordered overflow-hidden">
          <table className="w-full text-sm tabular" data-testid="pr-table">
            <thead className="text-[var(--text-3)] text-xs uppercase tracking-wider">
              <tr className="border-b border-[var(--border)]">
                <th className="text-left p-3 w-12">#</th>
                <th className="text-left p-3">Owner</th>
                <th className="text-right p-3">Starters</th>
                <th className="text-right p-3">Bench</th>
                <th className="text-right p-3">Picks</th>
                <th className="text-right p-3">Avg Age</th>
                <th className="text-right p-3">
                  <button type="button" onClick={() => setSortByTotal(v => !v)} data-testid="pr-sort-total"
                    title={sortByTotal ? "Sorted by total assets — click for win-now (projected finish) order" : "Sort by total asset value (players + picks)"}
                    className={`inline-flex items-center gap-1.5 justify-end uppercase tracking-wider transition-colors hover:text-[var(--accent)] ${sortByTotal ? "text-[var(--accent)]" : ""}`}>
                    <ArrowDownWideNarrow className={`w-3 h-3 ${sortByTotal ? "opacity-100" : "opacity-40"}`} />
                    Total <DmeTag />
                  </button>
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.roster_id}>
                  <tr className="border-b border-[var(--border)] hover:bg-white/[0.03] cursor-pointer" onClick={() => setExpanded(expanded === r.roster_id ? null : r.roster_id)} data-testid={`pr-row-${r.roster_id}`}>
                    <td className="p-3 font-semibold">
                      {r.rank === 1 ? <Trophy className="w-4 h-4 inline text-[var(--accent)]" /> : r.rank}
                    </td>
                    <td className="p-3 font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{r.owner_name}</span>
                        {(() => {
                          const t = contenderTier(r.playoff_pct, r.contention_rank ?? r.rank, data.rankings.length);
                          return t ? (
                            <span data-testid={`pr-tier-${r.roster_id}`} title={r.finish_why || undefined}
                              className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-wider rounded px-1.5 py-0.5 leading-none cursor-help"
                              style={{ color: t.color, border: `1px solid ${t.color}55`, background: `${t.color}14` }}>
                              {t.label}{r.finish_why && <Info className="w-2.5 h-2.5 opacity-70" />}
                            </span>
                          ) : null;
                        })()}
                        {r.underachiever && (
                          <span data-testid={`pr-flag-${r.roster_id}`} title="Elite roster value, but projected to finish in the bottom third — a soft weekly lineup is holding you back."
                            className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-wider rounded px-1.5 py-0.5 leading-none"
                            style={{ color: "#B45309", border: "1px solid #B4530955", background: "#B4530914" }}>
                            <AlertTriangle className="w-2.5 h-2.5" /> Missing a Few Pieces
                          </span>
                        )}
                        {r.playoff_pct != null && (
                          <span className="text-[9px] font-mono text-[var(--text-3)]" data-testid={`pr-playoff-${r.roster_id}`}>{r.playoff_pct}% playoffs</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-right">{formatValue(r.starter_value)}</td>
                    <td className="p-3 text-right text-[var(--text-2)]">{formatValue(r.bench_value)}</td>
                    <td className="p-3 text-right text-[var(--text-2)]" data-testid={`pr-picks-${r.roster_id}`}>{formatValue(r.picks_value)}</td>
                    <td className="p-3 text-right text-[var(--text-2)]" data-testid={`pr-age-${r.roster_id}`}>
                      {r.avg_age != null ? (
                        <span className="inline-flex items-center justify-end gap-1.5">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ageColor(r.avg_age) }}
                            title={r.avg_age < 27 ? "Young core" : r.avg_age < 28 ? "Aging — monitor" : "Old core — sell window"} data-testid={`pr-age-dot-${r.roster_id}`} />
                          {r.avg_age}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="p-3 text-right font-semibold text-[var(--accent)]">{formatValue(r.total_value)}</td>
                    <td className="p-3">{expanded === r.roster_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</td>
                  </tr>
                  {expanded === r.roster_id && (
                    <tr><td colSpan={8} className="bg-black/40 p-4">
                      {r.finish_why && (
                        <div className="mb-4 flex items-start gap-2 rounded-md border border-[var(--accent)]/25 bg-[var(--accent)]/5 px-3 py-2" data-testid={`pr-why-${r.roster_id}`}>
                          <Info className="w-4 h-4 mt-0.5 shrink-0 text-[var(--accent)]" />
                          <div className="min-w-0">
                            <div className="text-[9px] font-mono uppercase tracking-widest text-[var(--accent)] mb-0.5">Why this ranking</div>
                            <span className="text-xs text-[var(--text-2)] leading-relaxed">{r.finish_why}</span>
                          </div>
                        </div>
                      )}
                      {(() => {
                        const t = contenderTier(r.playoff_pct, r.contention_rank ?? r.rank, data.rankings.length);
                        const advice = tierAdvice(r, t, data.league?.upcoming_season);
                        return advice ? (
                          <div className="mb-4 flex items-start justify-between gap-3 rounded-md border border-[var(--border)] bg-black/[0.02] px-3 py-2"
                            style={{ borderLeft: `3px solid ${t.color}` }} data-testid={`pr-advice-${r.roster_id}`}>
                            <div className="flex items-start gap-2 min-w-0">
                              <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" style={{ color: t.color }} />
                              <span className="text-xs text-[var(--text-2)] leading-relaxed">{advice}</span>
                            </div>
                            <button onClick={() => copyGamePlan(r, t, advice)} data-testid={`pr-copy-plan-${r.roster_id}`}
                              className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-[var(--text-3)] hover:text-[var(--accent)] transition-colors"
                              title="Copy this team's game plan to share">
                              <Copy className="w-3.5 h-3.5" /> Copy plan
                            </button>
                          </div>
                        ) : null;
                      })()}
                      <div className="grid md:grid-cols-3 gap-6">
                        <div>
                          <div className="text-xs tracking-widest text-[var(--text-3)] mb-2">STARTERS</div>
                          <div className="space-y-1.5">
                            {r.starters.map(p => <RosterPlayer key={p.player_id} p={p} />)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs tracking-widest text-[var(--text-3)] mb-2">BENCH</div>
                          <div className="space-y-1.5">
                            {r.bench.map(p => <RosterPlayer key={p.player_id} p={p} dim />)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs tracking-widest text-[var(--text-3)] mb-2">DRAFT PICKS · PROJECTED</div>
                          <div className="space-y-1" data-testid={`pr-picks-detail-${r.roster_id}`}>
                            {(r.picks || []).map((pk) => (
                              <div key={`${pk.season}-${pk.round}-${pk.source_roster_id}`} className="flex justify-between text-xs text-[var(--text-2)] gap-2">
                                <span className="truncate">
                                  {pickLabel(pk)}
                                  {pk.original_owner && (
                                    <span className="text-[var(--text-3)]">{pk.is_own ? " · own" : ` · via ${pk.original_owner}`}</span>
                                  )}
                                </span>
                                <span className="tabular shrink-0">{formatValue(pk.value)}</span>
                              </div>
                            ))}
                            {(!r.picks || r.picks.length === 0) && <div className="text-xs text-[var(--text-3)]">No picks</div>}
                          </div>
                        </div>
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !data && !leagueId && (
        <div className="card-bordered p-12 text-center text-[var(--text-2)]">Pick a synced league to see power rankings.</div>
      )}
    </div>
  );
}
